import { sitesService } from '@/services/siteManagementService/siteManagementService';
import { RootState } from '@/store';
import { SiteFormData } from '@/types/project.types';
import {
  AllSitesCountDto,
  AllSitesCountResponseDto,
  AllSitesResponseDto,
  GetSiteByIdResponseDto,
  SiteByIdDto,
  SiteByProjectIdDto,
  SiteListItemDto,
  SitesDropdownResponse,
  SitesFilters,
} from '@/types/site.types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

interface SitesState {
  list: {
    data: SiteListItemDto[];
    total?: number;
    page?: number;
    limit?: number;
    totalPages?: number;
  };
  selected: SiteByIdDto | null;
  sitesStats: AllSitesCountDto | null;
  sitesByProject: SiteByProjectIdDto[];
  loading: boolean;
  error: string | null;
  loadingSiteStats: boolean;
  loadingSitesByProject: boolean;
  sitesDropdown: SitesDropdownResponse | null;
}

const initialState: SitesState = {
  list: {
    data: [],
  },
  selected: null,
  sitesStats: null,
  sitesByProject: [],
  loadingSiteStats: true,
  loadingSitesByProject: false,
  loading: false,
  error: null,
  sitesDropdown: null,
};

export const fetchSitesList = createAsyncThunk<
  AllSitesResponseDto,
  SitesFilters | undefined,
  { rejectValue: string }
>('sites/fetchAll', async (filters, { rejectWithValue }) => {
  try {
    return await sitesService.getSitesList(filters);
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error fetching sites list';

    return rejectWithValue(apiMessage);
  }
});

export const SitesDropdown = createAsyncThunk<SitesDropdownResponse, void, { rejectValue: string }>(
  'sites/dropdown',
  async (_, { rejectWithValue }) => {
    try {
      return await sitesService.sitesDropdown();
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Error fetching sites list';

      return rejectWithValue(apiMessage);
    }
  },
);

export const getSiteById = createAsyncThunk<
  GetSiteByIdResponseDto,
  string,
  {
    rejectValue: string;
  }
>('sites/getById', async (id: string, { rejectWithValue }) => {
  try {
    return await sitesService.getSiteById(id);
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error getting site by id';

    return rejectWithValue(apiMessage);
  }
});

export const toggleSiteStatus = createAsyncThunk<
  GetSiteByIdResponseDto,
  { id: number; status: boolean },
  { rejectValue: string }
>('sites/toggleStatus', async ({ id, status }, { rejectWithValue }) => {
  try {
    return await sitesService.toggleSiteStatus(id, status);
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || 'Failed to update site status');
  }
});

export const getSitesCount = createAsyncThunk<
  AllSitesCountResponseDto,
  void,
  {
    state: RootState;
    rejectValue: string;
  }
>(
  'sites/stats',
  async (_, { rejectWithValue }) => {
    try {
      return await sitesService.getSiteStats();
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Error getting sites count';

      return rejectWithValue(apiMessage);
    }
  },
  {
    condition: (_, { getState }) => {
      const { sitesStats } = getState().sites;
      return !sitesStats;
    },
  },
);

export const getSitesByProjectId = createAsyncThunk<
  SiteByProjectIdDto[],
  number,
  { state: RootState; rejectValue: string }
>('sites/getSitesByProjectId', async (projectId, { rejectWithValue }) => {
  try {
    const response = await sitesService.getSitesByProjectId(projectId);
    const updatedResponse = response.map((item) => ({
      ...item,
      projectId,
    }));
    return updatedResponse;
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error getting sites by project id';

    return rejectWithValue(apiMessage);
  }
});

export const updateSite = createAsyncThunk<
  GetSiteByIdResponseDto,
  Partial<SiteFormData>,
  {
    rejectValue: string;
  }
>('sites/update', async (data, { rejectWithValue }) => {
  try {
    return await sitesService.updateSite(data);
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error updating site';

    return rejectWithValue(apiMessage);
  }
});

const sitesSlice = createSlice({
  name: 'sites',
  initialState,
  reducers: {
    clearSitesError(state) {
      state.error = null;
    },
    clearSitesByProject(state) {
      state.sitesByProject = [];
    },
    clearSelectedSite(state) {
      state.selected = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchSitesList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSitesList.fulfilled, (state, action) => {
        state.loading = false;
        const { data, meta } = action.payload;

        // Always replace on page 1, append otherwise
        if (meta.page === 1) {
          state.list.data = data;
        } else {
          // Only append if not already present (prevent duplicates)
          const existingIds = new Set(state.list.data.map((s) => s.id));
          const newData = data.filter((s) => !existingIds.has(s.id));
          state.list.data = [...state.list.data, ...newData];
        }

        state.list.total = meta.total;
        state.list.page = meta.page;
        state.list.limit = meta.limit;
        state.list.totalPages = meta.totalPages;
      })
      .addCase(fetchSitesList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getSiteById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSiteById.fulfilled, (state, action) => {
        state.loading = false;
        state.selected = action.payload.data;
      })
      .addCase(getSiteById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder.addCase(toggleSiteStatus.fulfilled, (state, action) => {
      const updatedSite = action.payload.data;

      const listIndex = state.list.data.findIndex((s) => s.id === updatedSite.id);
      if (listIndex !== -1) {
        state.list.data[listIndex] = {
          ...state.list.data[listIndex],
          status: updatedSite.status,
        };
      }

      const projectIndex = state.sitesByProject.findIndex((s) => s.id === updatedSite.id);
      if (projectIndex !== -1) {
        state.sitesByProject[projectIndex] = {
          ...state.sitesByProject[projectIndex],
          status: updatedSite.status,
        };
      }

      if (state.selected && state.selected.id === updatedSite.id) {
        state.selected = {
          ...state.selected,
          status: updatedSite.status,
        };
      }
    });

    builder
      .addCase(getSitesCount.pending, (state) => {
        state.loadingSiteStats = true;
      })
      .addCase(getSitesCount.fulfilled, (state, action) => {
        state.loadingSiteStats = false;
        state.sitesStats = action.payload.data;
      })
      .addCase(getSitesCount.rejected, (state, action) => {
        state.loadingSiteStats = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getSitesByProjectId.pending, (state) => {
        state.loadingSitesByProject = true;
        state.error = null;
      })
      .addCase(getSitesByProjectId.fulfilled, (state, action) => {
        state.loadingSitesByProject = false;
        state.sitesByProject = action.payload;
      })
      .addCase(getSitesByProjectId.rejected, (state, action) => {
        state.loadingSitesByProject = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(SitesDropdown.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(SitesDropdown.fulfilled, (state, action) => {
        state.loading = false;
        state.sitesDropdown = action.payload;
      })
      .addCase(SitesDropdown.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateSite.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSite.fulfilled, (state, action) => {
        state.loading = false;
        state.selected = action.payload.data;

        // Update in list if present
        const index = state.list.data.findIndex((s) => s.id === action.payload.data.id);
        if (index !== -1) {
          // Merge updated data while preserving list structure
          state.list.data[index] = {
            ...state.list.data[index],
            name: action.payload.data.name,
            currency: action.payload.data.currency,
            status: action.payload.data.status,
          };
        }
      })
      .addCase(updateSite.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearSitesError, clearSelectedSite, clearSitesByProject } = sitesSlice.actions;
export default sitesSlice.reducer;
